// react imports
import { useState } from 'react';
import { Link } from 'react-router-dom';

// material-ui
import { useTheme } from '@mui/material/styles';
import { Box, Divider, FormControl, FormHelperText, Grid, IconButton, InputAdornment, OutlinedInput, Typography } from '@mui/material';
import { LoadingButton } from '@mui/lab';

// third party
import * as Yup from 'yup';
import { Formik } from 'formik';

// project imports
import AnimateButton from 'ui-component/extended/AnimateButton';
import { useAuthDoctorStore } from 'hooks/useAuthDoctorStore';

// assets
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import GoogleIcon from '@mui/icons-material/Google';
import PersonIcon from '@mui/icons-material/Person';

// ============================|| AuthRegister - REGISTER FORM ||============================ //

const AuthRegister = ({ ...others }) => {
    const theme = useTheme();
    const { registerDoctor } = useAuthDoctorStore();

    const [showPassword, setShowPassword] = useState(false);

    const handleClickShowPassword = () => setShowPassword(!showPassword);
    const handleMouseDownPassword = (event) => event.preventDefault();

    return (
        <Formik
            initialValues={{
                fname: '',
                lname: '',
                email: '',
                password: '',
                submit: null
            }}
            validationSchema={Yup.object().shape({
                fname: Yup.string().max(255).required('El nombre es requerido'),
                lname: Yup.string().max(255).required('El apellido es requerido'),
                email: Yup.string().email('Debes ingresar un email válido').max(255).required('El correo es requerido'),
                password: Yup.string()
                    .min(6, 'La contraseña debe tener al menos 6 caracteres')
                    .max(255)
                    .required('La contraseña es requerida')
            })}
            onSubmit={async (values, { setErrors, setStatus, setSubmitting }) => {
                try {
                    await registerDoctor({
                        fname: values.fname,
                        lname: values.lname,
                        email: values.email,
                        password: values.password
                    });
                    setStatus({ success: true });
                } catch (error) {
                    setStatus({ success: false });
                    setErrors({ submit: error.message });
                } finally {
                    setSubmitting(false);
                }
            }}
        >
            {({ errors, handleBlur, handleChange, handleSubmit, isSubmitting, touched, values }) => (
                <form noValidate onSubmit={handleSubmit} {...others}>
                    {/* Nombre + Apellido */}
                    <Grid container spacing={2} sx={{ mb: 0.5 }}>
                        <Grid item xs={6}>
                            <Typography variant="caption" sx={{ fontWeight: 700, letterSpacing: '0.08em', color: 'text.secondary' }}>
                                NOMBRE
                            </Typography>
                            <FormControl fullWidth error={Boolean(touched.fname && errors.fname)} sx={{ mt: 0.5 }}>
                                <OutlinedInput
                                    id="register-fname"
                                    type="text"
                                    value={values.fname}
                                    name="fname"
                                    onBlur={handleBlur}
                                    onChange={handleChange}
                                    placeholder="Dr. Enrique"
                                    inputProps={{}}
                                    sx={{ bgcolor: 'grey.100', borderRadius: 2 }}
                                />
                                {touched.fname && errors.fname && <FormHelperText error>{errors.fname}</FormHelperText>}
                            </FormControl>
                        </Grid>
                        <Grid item xs={6}>
                            <Typography variant="caption" sx={{ fontWeight: 700, letterSpacing: '0.08em', color: 'text.secondary' }}>
                                APELLIDO
                            </Typography>
                            <FormControl fullWidth error={Boolean(touched.lname && errors.lname)} sx={{ mt: 0.5 }}>
                                <OutlinedInput
                                    id="register-lname"
                                    type="text"
                                    value={values.lname}
                                    name="lname"
                                    onBlur={handleBlur}
                                    onChange={handleChange}
                                    placeholder="García"
                                    inputProps={{}}
                                    sx={{ bgcolor: 'grey.100', borderRadius: 2 }}
                                />
                                {touched.lname && errors.lname && <FormHelperText error>{errors.lname}</FormHelperText>}
                            </FormControl>
                        </Grid>
                    </Grid>

                    {/* Correo electrónico */}
                    <Box sx={{ mb: 0.5 }}>
                        <Typography variant="caption" sx={{ fontWeight: 700, letterSpacing: '0.08em', color: 'text.secondary' }}>
                            CORREO ELECTRÓNICO
                        </Typography>
                        <FormControl fullWidth error={Boolean(touched.email && errors.email)} sx={{ mt: 0.5 }}>
                            <OutlinedInput
                                id="register-email"
                                type="email"
                                value={values.email}
                                name="email"
                                onBlur={handleBlur}
                                onChange={handleChange}
                                placeholder="enrique.garcia@clinica.com"
                                sx={{ bgcolor: 'grey.100', borderRadius: 2 }}
                            />
                            {touched.email && errors.email && (
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
                                    <FormHelperText error sx={{ m: 0 }}>
                                        {errors.email}
                                    </FormHelperText>
                                    <Link
                                        to="/auth/login"
                                        style={{ fontSize: '0.75rem', color: theme.palette.error.main, textDecoration: 'underline' }}
                                    >
                                        ¿Quieres iniciar sesión?
                                    </Link>
                                </Box>
                            )}
                        </FormControl>
                    </Box>

                    {/* Contraseña */}
                    <Box sx={{ mb: 1 }}>
                        <Typography variant="caption" sx={{ fontWeight: 700, letterSpacing: '0.08em', color: 'text.secondary' }}>
                            CONTRASEÑA
                        </Typography>
                        <FormControl fullWidth error={Boolean(touched.password && errors.password)} sx={{ mt: 0.5 }}>
                            <OutlinedInput
                                id="register-password"
                                type={showPassword ? 'text' : 'password'}
                                value={values.password}
                                name="password"
                                onBlur={handleBlur}
                                onChange={handleChange}
                                placeholder="••••••••"
                                endAdornment={
                                    <InputAdornment position="end">
                                        <IconButton
                                            aria-label="toggle password visibility"
                                            onClick={handleClickShowPassword}
                                            onMouseDown={handleMouseDownPassword}
                                            edge="end"
                                            size="large"
                                        >
                                            {showPassword ? <Visibility /> : <VisibilityOff />}
                                        </IconButton>
                                    </InputAdornment>
                                }
                                inputProps={{}}
                                sx={{ bgcolor: 'grey.100', borderRadius: 2 }}
                            />
                            {touched.password && errors.password && <FormHelperText error>{errors.password}</FormHelperText>}
                        </FormControl>
                    </Box>

                    {errors.submit && (
                        <Box sx={{ mb: 2 }}>
                            <FormHelperText error>{errors.submit}</FormHelperText>
                        </Box>
                    )}

                    {/* Submit button */}
                    <AnimateButton>
                        <LoadingButton
                            disableElevation
                            loading={isSubmitting}
                            fullWidth
                            size="large"
                            type="submit"
                            variant="contained"
                            color="primary"
                            sx={{ borderRadius: 2, py: 1, fontWeight: 600, fontSize: '1rem' }}
                        >
                            Registrarse ahora
                        </LoadingButton>
                    </AnimateButton>

                    {/* Divider */}
                    <Divider sx={{ my: 1.5 }}>
                        <Typography variant="caption" sx={{ color: 'text.secondary', px: 1 }}>
                            O CONTINUAR CON
                        </Typography>
                    </Divider>

                    {/* Social buttons */}
                    <Grid container spacing={2}>
                        <Grid item xs={6}>
                            <LoadingButton
                                fullWidth
                                variant="outlined"
                                startIcon={<GoogleIcon />}
                                sx={{ borderRadius: 2, py: 0.8, color: 'text.primary', borderColor: 'divider' }}
                                disabled
                            >
                                Google
                            </LoadingButton>
                        </Grid>
                        <Grid item xs={6}>
                            <LoadingButton
                                fullWidth
                                variant="outlined"
                                startIcon={<PersonIcon />}
                                sx={{ borderRadius: 2, py: 0.8, color: 'text.primary', borderColor: 'divider' }}
                                disabled
                            >
                                SSO
                            </LoadingButton>
                        </Grid>
                    </Grid>

                    {/* Login link */}
                    <Box sx={{ textAlign: 'center', mt: 1.5 }}>
                        <Typography variant="body2" color="text.secondary">
                            ¿Ya tienes una cuenta?{' '}
                            <Link to="/auth/login" style={{ color: theme.palette.primary.main, textDecoration: 'none', fontWeight: 500 }}>
                                Inicia sesión
                            </Link>
                        </Typography>
                    </Box>
                </form>
            )}
        </Formik>
    );
};

export default AuthRegister;
